/**
 * Putting the finished sketch back into the answer.
 *
 * The tutor writes a brief; the animator writes the sketch minutes-of-tokens
 * later. Both the server (before persisting) and the client (mid-stream) have
 * to perform the same edit on the same text, so it lives here as one pure
 * function rather than twice, slightly differently.
 *
 * Blocks are matched on their brief. There is at most one animation per answer,
 * and the brief is the tutor's own sentence about it, so it is as stable a key
 * as anything short of an id the model would have to remember to emit.
 */

import { parseAnimSpec } from "./anim-spec.ts";

/** A ```anim fence: indent, fence run, info string, body, closing fence. */
const ANIM_FENCE = /^([ \t]*)(`{3,}|~{3,})[ \t]*anim[^\n]*\n([\s\S]*?)(\n[ \t]*\2)/gm;

export interface PendingAnimation {
  readonly brief: string;
  readonly title: string | null;
}

/** Every brief-only block in a draft, in the order the reader will meet them. */
export function findPendingAnimations(
  content: string,
): readonly PendingAnimation[] {
  const found: PendingAnimation[] = [];

  ANIM_FENCE.lastIndex = 0;
  let match = ANIM_FENCE.exec(content);

  while (match !== null) {
    const result = parseAnimSpec(match[3] ?? "");
    if (result.kind === "pending" && result.spec.brief) {
      found.push({
        brief: result.spec.brief,
        title: result.spec.title ?? null,
      });
    }
    match = ANIM_FENCE.exec(content);
  }

  return found;
}

/**
 * Fill in the sketch for the block carrying `brief`.
 *
 * Returns the content unchanged when there is no such block — which is the
 * normal case on every call but one, and on a stream that was aborted before
 * the block arrived.
 */
export function spliceAnimation(
  content: string,
  brief: string,
  code: string,
): string {
  return replaceBlock(content, brief, (indent, body) => {
    const sketch = code
      .trim()
      .split("\n")
      .map((line) => (line.length > 0 ? indent + line : line))
      .join("\n");
    return `${body}\n${indent}---\n${sketch}`;
  });
}

/**
 * Remove the block carrying `brief` entirely.
 *
 * Used when the animator and its reviewer could not agree on something worth
 * showing. A missing figure costs the reader a picture; a broken one costs them
 * their trust in the other figures.
 */
export function dropAnimation(content: string, brief: string): string {
  return replaceBlock(content, brief, () => null);
}

function replaceBlock(
  content: string,
  brief: string,
  rewrite: (indent: string, body: string) => string | null,
): string {
  ANIM_FENCE.lastIndex = 0;

  return content.replace(ANIM_FENCE, (whole, indent: string, fence: string, body: string, close: string) => {
    const result = parseAnimSpec(body);
    if (result.kind !== "pending" || result.spec.brief !== brief) return whole;

    const next = rewrite(indent, body);
    if (next === null) return "";
    return `${indent}${fence}anim\n${next}${close}`;
  });
}
